/**
 * Products the shopper opened at a store
 * ======================================
 * Written when a buy link is followed (affiliate-link-tracker.tsx), read by
 * the profile modal under "Products you opened". Lives in localStorage like
 * chat-history.ts and search-history.ts: the list never leaves the device, and
 * the profile lets the shopper remove one row or the whole list.
 *
 * This records that a store page was OPENED, nothing more. Whether anything
 * was bought is unknown to us and is never implied by this list.
 */

export interface ViewedProduct {
  id: string;
  title: string;
  /** Store name as shown: "Amazon", "bestbuy.ca". */
  store: string;
  /** The Inception page the click happened on, to get back to the comparison. */
  pageUrl?: string;
  timestamp: number;
  /** Full locale (`en-CA`), scoped by country like the history stores. */
  locale: string;
}

const STORAGE_KEY = 'viewed-products-v1';
const MAX_ITEMS = 30;

function safeRead(): ViewedProduct[] {
  if (typeof window === 'undefined') return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as ViewedProduct[]) : [];
  } catch (error) {
    console.error('Error reading viewed products:', error);
    return [];
  }
}

function safeWrite(items: ViewedProduct[]): void {
  if (typeof window === 'undefined') return;
  try {
    const trimmed = [...items].sort((a, b) => b.timestamp - a.timestamp).slice(0, MAX_ITEMS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
  } catch (error) {
    console.error('Error writing viewed products:', error);
  }
}

/** Newest first. */
export function getViewedProducts(): ViewedProduct[] {
  return safeRead().sort((a, b) => b.timestamp - a.timestamp);
}

/** Same country scoping as unified-history.ts: `en-CA` and `fr-CA` share one list. */
export function getViewedProductsForCountry(regionCode: string): ViewedProduct[] {
  return getViewedProducts().filter((p) => {
    const dash = p.locale.indexOf('-');
    return (dash === -1 ? p.locale : p.locale.slice(dash + 1)) === regionCode;
  });
}

/**
 * Record an opened product. Opening the same title at the same store again
 * moves it to the top instead of listing it twice.
 */
export function rememberViewedProduct(input: Omit<ViewedProduct, 'id' | 'timestamp'>): void {
  if (typeof window === 'undefined') return;
  const title = input.title?.trim();
  if (!title) return;
  const items = safeRead();
  const same = (p: ViewedProduct) => p.title === title && p.store === input.store && p.locale === input.locale;
  const next: ViewedProduct = {
    ...input,
    title,
    id: `vp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    timestamp: Date.now(),
  };
  // A re-open keeps its id and moves to the front: the write sorts by time,
  // and two opens in the same millisecond must still read newest first.
  const existing = items.findIndex(same);
  if (existing !== -1) {
    next.id = items[existing].id;
    items.splice(existing, 1);
  }
  safeWrite([next, ...items]);
}

export function removeViewedProduct(id: string): void {
  if (typeof window === 'undefined') return;
  safeWrite(safeRead().filter((p) => p.id !== id));
}

export function removeViewedProducts(ids: string[]): void {
  if (typeof window === 'undefined' || ids.length === 0) return;
  const doomed = new Set(ids);
  safeWrite(safeRead().filter((p) => !doomed.has(p.id)));
}

export function clearViewedProductsForCountry(regionCode: string): void {
  removeViewedProducts(getViewedProductsForCountry(regionCode).map((p) => p.id));
}

export function clearViewedProducts(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing viewed products:', error);
  }
}

/**
 * The store a buy link opens, for display. Buy links point at our `/go` hop
 * with the clean destination in `u`; a direct link is read as is. Amazon is
 * named outright (its many hosts all read "Amazon"); any other store is its
 * host without `www.`, which is how shoppers know it ("bestbuy.ca").
 */
export function storeLabelForLink(href: string, isAmazon: boolean, merchantDomain?: unknown): string {
  if (isAmazon) return 'Amazon';
  if (typeof merchantDomain === 'string' && merchantDomain.trim()) {
    return merchantDomain.trim().replace(/^www\./, '');
  }
  try {
    const url = new URL(href, 'https://inception.local');
    const inner = url.searchParams.get('u');
    const dest = inner ? new URL(inner) : url;
    const host = dest.hostname.toLowerCase().replace(/^www\./, '');
    return host && host !== 'inception.local' ? host : '';
  } catch {
    return '';
  }
}
